import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { FormikProps } from "formik/dist/types";
import { useRouter } from "next/router";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import KeywordSuggestionsDropdown from "src/components/common/KeywordSuggestionsDropdown";
import InsightsEditor from "src/components/dashboard/brief/rightSection/InsightsEditor";
import { ArrowLeft } from "src/icons/arrow-left";
import { ExclamationCircleColored } from "src/icons/exclamation-circle-colored";
import { MagicWandIcon } from "src/icons/magic-wand-icon";
import {
  authorizeWithSearchConsole,
  getActivePlan,
} from "src/slices/auth/thunks";
import { languages, locations, usStates } from "src/slices/brief/helpers";
import { clearSuggestions } from "src/slices/keywordSuggestions";
import {
  selectKeywordSuggestions,
  selectKeywordSuggestionsError,
  selectKeywordSuggestionsLoading,
} from "src/slices/keywordSuggestions/selectors";
import { fetchKeywordSuggestions } from "src/slices/keywordSuggestions/thunks";
import { getProjectById, getProjects, projects } from "src/slices/projects";
import { createProject } from "src/slices/projects/thunks";
import { selectOptimizeContentLoading } from "src/slices/suggestions/selectors";
import {
  generateAutomatedInstructionsForOptimize,
  scrapeArticleForOptimize,
} from "src/slices/suggestions/thunks";
import { snackAdded } from "src/slices/system";
import { getSearchConsoleData, getSites } from "src/slices/system/thunks";
import { useDispatch, useSelector } from "src/store";

type Props = {
  formik: FormikProps<{
    keyword: string;
    location: (typeof locations)[0];
    state: (typeof usStates)[0] | null;
    language: string;
    websiteUrl: string;
    generateArticle: boolean;
    interlinking: boolean;
    stats: boolean;
    insights: string;
    articleLink: string;
    humanize: boolean;
    thoughtLeadership: boolean;
    aiResearchAgent: boolean;
    aiEvalRefineAgent: boolean;
    generateAutomatedInsights: boolean;
  }>;
  articleChoice: string;
  isEEATMode?: boolean;

  isSubmitted: (type: string, projectId?: string) => void;
  onClose: () => void;
  onBack: () => void;
};

export const Article = ({
  formik,
  articleChoice,
  isSubmitted,
  onClose,
  onBack,
  isEEATMode,
}: Props) => {
  const router = useRouter();

  const dispatch = useDispatch();
  const [currentPlan, setCurrentPlan] = useState<any>({});
  const [isInterLinkingAdded, setIsInterLinkingAdded] = useState(false);
  const [projectId, setProjectId] = useState("");
  const Project = useSelector(getProjectById(projectId));
  const Projects = useSelector(projects);
  const [gscData, setGscData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSite, setSelectedSite] = useState("");
  const [isIntegrateSearchConsole, setIsIntegrateSearchConsole] =
    useState(false);
  const [loadingText, setLoadingText] = useState("Connecting Domain");
  const [isClicked, setIsClicked] = useState({
    oneClick: false,
    advanced: false,
    update: false,
  });

  // Keyword suggestions state
  const keywordInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const keywordSuggestions = useSelector(selectKeywordSuggestions);
  const suggestionsLoading = useSelector(selectKeywordSuggestionsLoading);
  const suggestionsError = useSelector(selectKeywordSuggestionsError);
  const isOptimizeContentLoading = useSelector(selectOptimizeContentLoading);

  const getCurrentPlan = async () => {
    const currentPlan = (await dispatch(getActivePlan())) as any;
    setCurrentPlan(currentPlan?.payload?.plan);
  };
  useEffect(() => {
    getCurrentPlan();
  }, []);

  const [insights, setInsights] = useState(false);
  const [isWritingInstructions, setIsWritingInstructions] = useState(false);
  const [statsToggletext, setStatsToggletext] = useState(
    "We'll research and add relevant stats and quotes to your AI article if they match your outline."
  );
  const [interLinkingToggletext, setInterLinkingToggletext] = useState("");
  const [placeholderText, setPlaceholderText] = useState(
    "Add your insights here..."
  );

  const [humanizeToggletext, setHumanizeToggletext] = useState(
    `Once your article is generated, our system proofreads, rewrites, and adjusts the text using AI to sound more natural and human-like, helping it avoid AI detection.
    <br /><br />
    Note: Headings and structure may change to improve readability. This is an automated process.`
  );
  const [thoughtLeadershipToggletext, setThoughtLeadershipToggletext] =
    useState(
      "We'll research relevant thought leadership insights and add them to the article to enhance its value and meet EEAT principles"
    );

  const [aiResearchAgentToggletext, setAiResearchAgentToggletext] = useState(
    "Enable AI-driven research to enhance your article with relevant statistics, expert insights, case studies, emerging trends, and even data from your own website. This feature ensures your content is backed by high-quality, authoritative information, making it more engaging and credible."
  );
  const [aiEvalRefineAgentToggletext, setAiEvalRefineAgentToggletext] =
    useState(
      "Enable AI-powered evaluation to improve your AI-generated article based on analysis, data enrichment, and refinement. This feature rewrites and fine-tunes your content for better clarity, authority, and SEO minimizing editing on your end."
    );
  const isAiEvalRefineAgentAvailableAtPlan = true;
  const isAiResearchAgentAvailableAtPlan = true;
  useEffect(() => {
    if (formik.values?.insights) {
      setInsights(true);
      formik.setFieldValue("insights", formik.values.insights);
    }
  }, [formik.values.insights]);

  useEffect(() => {
    if (insights) {
      setPlaceholderText(
        "Add your personal experience, know-how, data and quotes to enrich the AI article. Add each insight in a separate row for better clarity."
      );
    } else {
      setPlaceholderText(
        "SEOwind will read and analyze content you paste here to learn how to communicate in your voice."
      );
    }
  }, [insights]);

  useEffect(() => {
    if (Project?.isGSCIntegrated) {
      setIsInterLinkingAdded(true);
      formik.setFieldValue("interlinking", true);

      setInterLinkingToggletext(
        "We'll add relevant internal links to your article by analyzing your article, data from Google and GSC. To integrate with your GSC click on setup GSC."
      );
    } else if (Project === undefined) {
      setIsInterLinkingAdded(false);
      formik.setFieldValue("interlinking", false);

      setInterLinkingToggletext(
        "We'll add relevant internal links to your article by analyzing your article, data from Google and GSC. To integrate with your GSC click on setup GSC."
      );
    } else {
      setIsInterLinkingAdded(false);
      formik.setFieldValue("interlinking", false);

      setInterLinkingToggletext(
        "We'll add relevant internal links to your article by analyzing your article, data from Google and GSC. Upgrade your plan to unlock the feature."
      );
    }
  }, [Project]);

  const handleLocationChange = (event: ChangeEvent<any>, value) => {
    formik.setFieldValue("location", value);
    localStorage.setItem("brief-location", JSON.stringify(value));

    // Reset state when location changes and it's not US
    if (value?.location_name !== "United States") {
      formik.setFieldValue("state", null);
      localStorage.removeItem("brief-state");
    }
  };

  const handleStateChange = (event: ChangeEvent<any>, value: any) => {
    formik.setFieldValue("state", value);
    if (value) {
      localStorage.setItem("brief-state", JSON.stringify(value));
    } else {
      localStorage.removeItem("brief-state");
    }
  };

  const handleLanguageChange = (event: ChangeEvent<any>) => {
    formik.handleChange(event);
    localStorage.setItem("brief-language", event.target.value);
  };

  const handleInsightsAdded = (event: ChangeEvent<any>) => {
    setInsights(event.target.checked);
    formik.setFieldValue("generateAutomatedInsights", event.target.checked);

    // Clear insights field when switching to auto-generate mode
    if (event.target.checked) {
      formik.setFieldValue("insights", "");
    }
  };

  const handleSelectChange = async (e: any) => {
    setLoadingText("Connecting Domain");
    setSelectedSite(e.target.value);
    setIsLoading(true);

    // Use existing project or create one if needed
    const currentProjectId = await createProjectIfNeeded();
    if (!currentProjectId) {
      setIsLoading(false);
      return; // Error already shown by createProjectIfNeeded
    }

    const res: any = await dispatch(
      getSearchConsoleData({
        siteUrl: e.target.value,
        projectId: currentProjectId,
      })
    );
    if (res?.payload?.success == true && res?.payload?.type == "success") {
      dispatch(getProjects());
      setIsLoading(false);
      await dispatch(
        snackAdded({
          message: "Domain connected successfully",
          type: "success",
        })
      );
      setIsInterLinkingAdded(true);
      setGscData([]);
      formik.setFieldValue("interlinking", true);
    } else {
      setIsLoading(false);
    }

    setIsLoading(false);
    await dispatch(
      snackAdded({
        message: "Domain connected successfully",
        type: "success",
      })
    );
    setIsInterLinkingAdded(true);
    setGscData([]);
    formik.setFieldValue("interlinking", true);
  };
  const setUpGSC = async () => {
    setIsLoading(true);
    setLoadingText("Authorizing");
    let data = [];

    // Create project if needed first
    const currentProjectId = await createProjectIfNeeded();
    if (!currentProjectId) {
      setIsLoading(false);
      return; // Error already shown by createProjectIfNeeded
    }

    const resData: any = await dispatch(authorizeWithSearchConsole());

    const popup = window.open(
      resData.payload.authorizeUrl,
      "newWindow",
      "width=500,height=600,toolbar=0,menubar=0,location=0"
    );
    if (!popup) {
      setIsLoading(false);
      dispatch(
        snackAdded({
          message:
            "Could not open the Google authorization window. Please allow popups for this site and try again.",
          type: "error",
        })
      );
      return;
    }
    const timer = await setInterval(async function () {
      if (popup.closed) {
        clearInterval(timer);
        //create project

        const secondResult: any = await dispatch(
          getSites({
            projectId: currentProjectId,
            projectDomain: formik.values.websiteUrl,
          })
        );

        if (
          secondResult.payload.success == true &&
          secondResult.payload.type == "success"
        ) {
          data = secondResult.payload.data;
          setGscData(data);
          setIsLoading(false);
        } else {
          data = [];
          setGscData(data);
          setIsLoading(false);
          dispatch(
            snackAdded({
              message: "No Domain found for this account",
              type: "success",
            })
          );
        }
      }
    }, 1000);
  };

  useEffect(() => {
    dispatch(getProjects());

    // Project will be created lazily when needed (GSC setup or write instructions)
    // No localStorage reads to avoid cross-account data contamination
  }, []);

  // Manage generateAutomatedInsights based on insights content
  useEffect(() => {
    if (formik.values?.insights && formik.values.insights.trim()) {
      // If user has manually added insights, disable auto-generation
      setInsights(true);
      formik.setFieldValue("insights", formik.values.insights);
      formik.setFieldValue("generateAutomatedInsights", false);
    }
  }, [formik.values.insights]);

  // Set AI Research Agent to enabled by default for onboarding
  useEffect(() => {
    if (isAiResearchAgentAvailableAtPlan) {
      formik.setFieldValue("aiResearchAgent", true);
      setAiResearchAgentToggletext(
        "Enable AI-driven research to enhance your article with relevant statistics, expert insights, case studies, emerging trends, and even data from your own website. This feature ensures your content is backed by high-quality, authoritative information, making it more engaging and credible."
      );
    }
  }, []);

  {
    /* projects commented out */
  }

  const checkError = async () => {
    if (Object.keys(formik.errors).length === 0) {
      formik.setFieldValue("generateArticle", true);
      isSubmitted("oneclick", projectId);
      setIsClicked({ ...isClicked, oneClick: true });
    }
  };

  // Keyword suggestions handlers
  const handleSuggestionsClick = () => {
    const keyword = formik.values.keyword?.trim();
    if (!keyword || keyword.length < 2) {
      return;
    }

    const locationName =
      formik.values.location?.location_name || "United States";
    const languageCode = formik.values.language || "English";

    // Convert language name to code
    const languageObj = languages.find(
      (lang) => lang.languageName === languageCode
    );
    const languageCodeForAPI = languageObj?.languageCode || "en";

    dispatch(
      fetchKeywordSuggestions({
        keyword,
        locationName,
        languageCode: languageCodeForAPI,
      })
    );
    setSuggestionsOpen(true);
  };

  const handleSuggestionSelect = (selectedKeyword: string) => {
    formik.setFieldValue("keyword", selectedKeyword);

    setSuggestionsOpen(false);
    dispatch(clearSuggestions());
  };

  const handleSuggestionsClose = () => {
    setSuggestionsOpen(false);
    dispatch(clearSuggestions());
  };

  // Handle manual typing in the keyword field
  const handleKeywordChange = (event: ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(event);
    // Close suggestions when user manually types
    if (suggestionsOpen) {
      handleSuggestionsClose();
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsOpen) {
        const target = event.target as Node;
        const isClickInInput = keywordInputRef.current?.contains(target);
        const isClickInDropdown = dropdownRef.current?.contains(target);

        // Only close if click is outside both input and dropdown
        if (!isClickInInput && !isClickInDropdown) {
          handleSuggestionsClose();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [suggestionsOpen]);

  // Reusable function to create project when needed
  const createProjectIfNeeded = async () => {
    if (!formik.values.websiteUrl) {
      dispatch(
        snackAdded({
          message: "Please provide your website URL first",
          type: "error",
          duration: 3000,
        })
      );
      return null;
    }

    if (projectId) {
      return projectId; // Project already exists
    }

    try {
      // Show user that project is being created
      dispatch(
        snackAdded({
          message: "Creating project...",
          type: "info",
          duration: 2000,
        })
      );

      const resCreateProject = await dispatch(
        createProject({
          projectName: formik.values.websiteUrl,
          projectDomain: formik.values.websiteUrl,
          AImodel: "claude",
        })
      );

      const newProjectId = resCreateProject?.payload?.data?.projectId;
      if (newProjectId) {
        // Store in component state only, not localStorage to avoid cross-account issues
        setProjectId(newProjectId);

        // Refresh projects list
        dispatch(getProjects());

        dispatch(
          snackAdded({
            message: "Project created successfully",
            type: "success",
            duration: 2000,
          })
        );

        return newProjectId;
      }
    } catch (error) {
      console.error("Failed to create project:", error);
      dispatch(
        snackAdded({
          message: "Failed to create project. Please try again.",
          type: "error",
          duration: 3000,
        })
      );
    }

    return null;
  };

  // Function to extract clean, structured outline from markdown content
  // (Copied exactly from optimize content component)
  const extractOutlineFromMarkdown = (markdownText: string): string => {
    if (!markdownText) return "";

    const lines = markdownText.split("\n");
    const outlineLines: string[] = [];

    // Patterns to filter out promotional/implementation content
    const skipPatterns = [
      /subscribe|newsletter|inbox|email/i,
      /follow us|social media|linkedin|twitter/i,
      /download|ebook|whitepaper|resource/i,
      /contact|sales|demo|trial/i,
      /advertisement|sponsored|affiliate/i,
      /click here|learn more|read more/i,
      /\b(yarooms|specific product names)\b/i, // Filter out specific product mentions
      /^[-*+]\s*(provide|use|create|implement|add|install|arrange|train)\b/i, // Implementation details
      /^[-*+]\s*\w+\s*(rates?|percentages?|statistics?|metrics?|numbers?)/i, // Specific metrics
      /^[-*+]\s*(fitness|nutrition|sleep|apps?|platforms?|tools?)\b/i, // Tactical tools
    ];

    // Quality filters for content
    const isQualityContent = (line: string): boolean => {
      const trimmed = line.trim();

      // Skip empty lines
      if (!trimmed) return false;

      // Skip very short lines (likely incomplete)
      if (trimmed.length < 10) return false;

      // Skip lines that are just numbers or symbols
      if (/^[\d\s\-#*+.]+$/.test(trimmed)) return false;

      // Skip promotional content
      if (skipPatterns.some((pattern) => pattern.test(trimmed))) return false;

      return true;
    };

    // Extract headings with hierarchy preservation
    const extractHeadings = (line: string): boolean => {
      const trimmed = line.trim();
      const headingMatch = trimmed.match(/^(#{1,4})\s+(.+)$/);

      if (headingMatch) {
        const level = headingMatch[1].length;
        const title = headingMatch[2].trim();

        // Skip headings that are just promotional or meta content
        if (skipPatterns.some((pattern) => pattern.test(title))) return false;

        // Focus on strategic content (avoid overly specific implementation)
        if (level <= 3 && title.length > 5) {
          // Only include h1, h2, h3 with meaningful titles
          return true;
        }
      }
      return false;
    };

    // Extract strategic bullet points (avoid implementation details)
    const isStrategicBulletPoint = (line: string): boolean => {
      const trimmed = line.trim();
      const bulletMatch = trimmed.match(/^[-*+]\s+(.+)$/);

      if (bulletMatch) {
        const content = bulletMatch[1].trim();

        // Focus on high-level strategic points, not implementation details
        const strategicKeywords = [
          /\b(strategy|approach|framework|principle|benefit|advantage|outcome|result|impact|improvement)\b/i,
          /\b(increased?|improved?|enhanced?|reduced?|better|higher|lower|more effective)\b/i,
          /\b(organizational|company|business|workplace|employee|team|culture)\b/i,
        ];

        // Include if it contains strategic keywords and isn't too tactical
        return (
          strategicKeywords.some((pattern) => pattern.test(content)) &&
          !/^(provide|use|create|implement|add|install|arrange|train|set up)/i.test(
            content
          )
        );
      }
      return false;
    };

    let currentSection = "";

    for (const line of lines) {
      if (!isQualityContent(line)) continue;

      const trimmedLine = line.trim();

      // Extract headings
      if (extractHeadings(line)) {
        outlineLines.push(trimmedLine);
        currentSection = trimmedLine;
      }
      // Extract strategic bullet points only for relevant sections
      else if (isStrategicBulletPoint(line) && currentSection) {
        // Only include strategic bullets under major sections
        const headingLevel = currentSection.match(/^#+/)?.[0]?.length || 0;
        if (headingLevel <= 2) {
          // Only for h1 and h2 sections
          outlineLines.push(trimmedLine);
        }
      }
    }

    // Clean up and organize the outline
    const cleanOutline = outlineLines
      .filter((line, index) => {
        // Remove duplicate consecutive lines
        if (index > 0 && line === outlineLines[index - 1]) return false;
        return true;
      })
      .slice(0, 50); // Limit to prevent overly long outlines

    return cleanOutline.join("\n");
  };

  // Automated instructions generation handler for update mode
  const handleWriteInstructions = async () => {
    const articleLink = formik.values.articleLink;

    if (!articleLink) {
      dispatch(
        snackAdded({
          message: "Please provide an article link to generate instructions",
          type: "error",
          duration: 3000,
        })
      );
      return;
    }

    setIsWritingInstructions(true);

    // Create project if needed
    const currentProjectId = await createProjectIfNeeded();
    if (!currentProjectId) {
      setIsWritingInstructions(false);
      return; // Error already shown by createProjectIfNeeded
    }

    // Ensure projectId is a string, not an object
    const projectIdString =
      typeof currentProjectId === "string"
        ? currentProjectId
        : currentProjectId?.projectId || currentProjectId;

    try {
      // First, scrape the article to get title, description, and content
      dispatch(
        snackAdded({
          message: "Scraping article content...",
          type: "info",
          duration: 2000,
        })
      );

      const scrapeResult = await dispatch(
        scrapeArticleForOptimize(articleLink)
      ).unwrap();

      if (!scrapeResult.success) {
        throw new Error(
          scrapeResult.error || "Failed to scrape article content"
        );
      }

      const articleContent = scrapeResult.data?.[0] || "";
      const articleTitle = scrapeResult.data?.[1] || "";
      const articleDescription = scrapeResult.data?.[2] || "";

      dispatch(
        snackAdded({
          message: "Generating instructions...",
          type: "info",
          duration: 2000,
        })
      );

      // Extract outline from the markdown content
      const outline = extractOutlineFromMarkdown(articleContent);

      // Generate instructions using the scraped data
      const instructions = await dispatch(
        generateAutomatedInstructionsForOptimize({
          projectId: projectIdString,
          title: articleTitle,
          description: articleDescription,
          outline: outline,
          content: articleContent,
        })
      ).unwrap();

      // Update the insights field with generated instructions
      const currentInsights = formik.values.insights || "";
      let updatedInsights = "";

      if (currentInsights.trim()) {
        // If there are existing insights, add generated instructions below them
        updatedInsights = currentInsights + "\n\n---\n\n" + instructions;
      } else {
        // If no existing insights, add generated instructions at the top
        updatedInsights = instructions;
      }

      formik.setFieldValue("insights", updatedInsights);

      dispatch(
        snackAdded({
          message: "Instructions generated successfully",
          type: "success",
          duration: 3000,
        })
      );
    } catch (error) {
      console.error("Failed to generate instructions:", error);
      dispatch(
        snackAdded({
          message: "Failed to generate instructions. Please try again.",
          type: "error",
          duration: 3000,
        })
      );
    } finally {
      setIsWritingInstructions(false);
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 4 }}>
      <Box display={"flex"} alignItems={""} flexDirection={"column"}>
        <Box sx={{ pt: "25px" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography
              color="#121828"
              /* Heading/H1 */
              fontFamily="Inter"
              fontSize="20px"
              fontStyle="normal"
              fontWeight="500"
              lineHeight="normal"
            >
              {articleChoice === "write"
                ? "Create Article"
                : "Update your article"}
            </Typography>
            <Typography
              fontFamily="Inter"
              fontSize="14px"
              fontStyle="normal"
              fontWeight="400"
              color="#7B85AD"
            >
              Step 2 of 2
            </Typography>
          </Box>

          <Box sx={{ width: "450px", pt: 3 }}>
            <TextField
              fullWidth
              label="Focus keyword"
              name="keyword"
              type="text"
              value={formik.values.keyword}
              defaultValue={formik.values.keyword}
              onBlur={formik.handleBlur}
              onChange={handleKeywordChange}
              inputRef={keywordInputRef}
              helperText={
                formik.errors.keyword && formik.touched
                  ? formik.errors.keyword
                  : undefined
              }
              sx={{ "& .MuiFormHelperText-root": { color: "#D14343" } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip
                      title={
                        !formik.values.keyword?.trim()
                          ? "Enter a keyword to get suggestions"
                          : formik.values.keyword.length < 2
                          ? "Keyword must be at least 2 characters"
                          : suggestionsLoading
                          ? "Loading suggestions..."
                          : "Get keyword suggestions"
                      }
                    >
                      <span>
                        <IconButton
                          onClick={handleSuggestionsClick}
                          disabled={
                            !formik.values.keyword?.trim() ||
                            formik.values.keyword.length < 2 ||
                            suggestionsLoading
                          }
                          sx={{
                            color: "#5048E5",
                            "&:hover": {
                              backgroundColor: "rgba(80, 72, 229, 0.1)",
                            },
                            "&:disabled": {
                              color: "#ccc",
                            },
                          }}
                        >
                          {suggestionsLoading ? (
                            <CircularProgress
                              size={20}
                              sx={{ color: "#5048E5" }}
                            />
                          ) : (
                            <LightbulbIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />

            {/* Keyword Suggestions Dropdown */}
            <KeywordSuggestionsDropdown
              anchorEl={keywordInputRef.current}
              open={suggestionsOpen}
              suggestions={keywordSuggestions}
              loading={suggestionsLoading}
              error={suggestionsError}
              onSuggestionSelect={handleSuggestionSelect}
              onClose={handleSuggestionsClose}
              dropdownRef={dropdownRef}
              width="450px"
            />
          </Box>
          {articleChoice === "update" && (
            <Box sx={{ pt: 3 }}>
              <TextField
                fullWidth
                label="Article Link"
                name="articleLink"
                type="text"
                value={formik.values.articleLink}
                defaultValue={formik.values.articleLink}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                helperText={
                  formik.errors.articleLink && formik.touched
                    ? formik.errors.articleLink
                    : undefined
                }
                sx={{ "& .MuiFormHelperText-root": { color: "#D14343" } }}
              />
            </Box>
          )}
          <Grid container gap={1} sx={{ pt: 3 }}>
            <Grid>
              <FormControl>
                <Autocomplete
                  id="location"
                  options={locations}
                  getOptionLabel={(option: {
                    location_name: string;
                    country_iso_code: string;
                  }) => option.location_name}
                  sx={{ width: 220 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Location"
                      helperText={
                        formik.errors.location && formik.touched
                          ? formik.errors.location
                          : undefined
                      }
                      sx={{ "& .MuiFormHelperText-root": { color: "#D14343" } }}
                    />
                  )}
                  value={formik.values.location}
                  onChange={handleLocationChange}
                  renderOption={(props, option) => (
                    <MenuItem
                      key={option.location_name}
                      value={option.location_name}
                      {...props}
                    >
                      {option.location_name}
                    </MenuItem>
                  )}
                />
              </FormControl>
            </Grid>
            <Grid flex={1}>
              <FormControl fullWidth>
                <InputLabel id="language-label">Language</InputLabel>
                <Select
                  labelId="language-label"
                  id="language"
                  value={formik.values.language}
                  label="language"
                  name="language"
                  onChange={handleLanguageChange}
                  defaultValue="English"
                >
                  {languages
                    .sort((a, b) => {
                      if (a.languageName > b.languageName) {
                        return 1;
                      } else if (a.languageName < b.languageName) {
                        return -1;
                      } else {
                        return 0;
                      }
                    })
                    .map((value) => (
                      <MenuItem
                        key={value.languageName}
                        value={value.languageName}
                      >
                        {value.languageName === "Arabic" ||
                        value.languageName === "Hebrew"
                          ? value.languageName + " " + "(Beta)"
                          : value.languageName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* US State Dropdown - appears below when United States is selected */}
          <Collapse
            in={formik.values.location?.location_name === "United States"}
            timeout={300}
          >
            <Grid container gap={1} sx={{ pt: 3 }}>
              <Grid>
                <FormControl>
                  <Autocomplete
                    id="state"
                    options={usStates}
                    getOptionLabel={(option: (typeof usStates)[0]) =>
                      option.state_name
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.state_code === value?.state_code
                    }
                    PopperComponent={(props) => (
                      <Popper {...props} placement="bottom-start" />
                    )}
                    sx={{
                      width: 220,
                      "& .MuiOutlinedInput-root": {
                        boxShadow:
                          formik.values.location?.location_name ===
                          "United States"
                            ? "0 0 0 2px rgba(80, 72, 229, 0.1)"
                            : "none",
                        transition: "box-shadow 0.3s ease-in-out",
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="State (Optional)"
                        placeholder="Select a state for more targeted results"
                        helperText={
                          formik.errors.state && formik.touched
                            ? formik.errors.state
                            : "Leave blank for country-wide targeting"
                        }
                        sx={{
                          "& .MuiFormHelperText-root": {
                            color:
                              formik.errors.state && formik.touched
                                ? "#D14343"
                                : "#7B85AD",
                          },
                        }}
                      />
                    )}
                    value={formik.values.state}
                    onChange={handleStateChange}
                    renderOption={(props, option) => (
                      <MenuItem
                        key={option.state_code}
                        value={option.state_name}
                        {...props}
                      >
                        {option.state_name}
                      </MenuItem>
                    )}
                    clearOnEscape
                    disableClearable={false}
                  />
                </FormControl>
              </Grid>
              <Grid flex={1}>
                <Box />
              </Grid>
            </Grid>
          </Collapse>

          <Box sx={{ width: "450px", pt: 3 }}>
            <TextField
              fullWidth
              label="Your website URL"
              name="websiteUrl"
              type="text"
              value={formik.values.websiteUrl}
              defaultValue={formik.values.websiteUrl}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              helperText={
                formik.errors.websiteUrl && formik.touched
                  ? formik.errors.websiteUrl
                  : undefined
              }
              sx={{ "& .MuiFormHelperText-root": { color: "#D14343" } }}
            />
          </Box>

          <Box sx={{ pt: 3 }}>
            <Typography
              gutterBottom
              sx={{ fontWeight: 500, fontSize: "1.1rem", mr: 1 }}
            >
              {articleChoice === "write"
                ? "Create article with :"
                : "Update article with :"}
            </Typography>

            {/* interlinking toggle */}
            <Box
              display={"flex"}
              justifyContent="space-between"
              sx={{
                alignItems: "center",
              }}
            >
              <Box display={"flex"} alignItems={"center"}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        onChange={formik.handleChange}
                        // disabled={!isInterLinkingAdded}
                        checked={formik.values.interlinking}
                        // defaultChecked={interLinkingChecked}
                        name="interlinking"
                        value={formik.values.interlinking}
                        disabled={!isInterLinkingAdded}
                      />
                    }
                    label="Internal Linking"
                  />
                </FormGroup>
                <Tooltip
                  title={
                    <div
                      dangerouslySetInnerHTML={{
                        __html:
                          interLinkingToggletext ||
                          "We'll add internal links to your AI article if they match your outline.",
                      }}
                    />
                  }
                  placement="top"
                >
                  <InfoIcon sx={{ fontSize: "16px", ml: -1.5 }} />
                </Tooltip>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  ml: "auto",
                }}
              >
                {isLoading ? (
                  <Box sx={{ width: "100%", pt: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: "400",
                        fontFamily: "Inter",
                        fontSize: "14px",
                        color: "#7B85AD",
                      }}
                    >
                      {loadingText}
                    </Typography>
                    <LinearProgress
                      sx={{
                        height: "4px",
                        borderRadius: "4px",
                        backgroundColor: "#E6E8F0",
                        width: "150px",
                        mt: "4px",
                      }}
                    />
                  </Box>
                ) : gscData.length > 0 ? (
                  <Box sx={{ width: "100%" }}>
                    <FormControl fullWidth>
                      <InputLabel id="selectSite">Select Site</InputLabel>
                      <Select
                        sx={{
                          width: "30%",
                          minWidth: "200px",
                          // padding: "-10px !important",
                          paddingTop: "0px !important",
                          paddingBottom: "0px !important",
                        }}
                        labelId="selectSite"
                        id="selectSite"
                        // value={age}
                        value={selectedSite}
                        label="Select Site"
                        placeholder="Select Site"
                        onChange={(e) => {
                          handleSelectChange(e);
                        }}
                      >
                        {gscData.map((site: any) => (
                          <MenuItem value={site} key={site}>
                            {site}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                ) : (
                  <Link
                    component="button"
                    type="button"
                    sx={{
                      ml: "auto",
                      cursor: "pointer",

                      color: "#5048E5",
                      textDecoration: "none",
                      "&:hover": {
                        textDecoration: "None",
                      },
                      fontSize: "14px",
                      fontWeight: "400",
                      fontFamily: "Inter",

                      ":disabled": {
                        color: "#B0B7C3",
                        cursor: "not-allowed",
                      },
                    }}
                    disabled={isInterLinkingAdded}
                    onClick={() => {
                      setUpGSC();
                    }}
                  >
                    {isInterLinkingAdded ? selectedSite : "Setup GSC"}
                  </Link>
                )}
              </Box>
            </Box>

            {/* AI Research Agent */}
            <Box display={"flex"} alignItems={"center"}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      onChange={formik.handleChange}
                      disabled={!isAiResearchAgentAvailableAtPlan}
                      checked={formik.values?.aiResearchAgent}
                      name="aiResearchAgent"
                      value={formik.values?.aiResearchAgent}
                    />
                  }
                  label="AI Research Agent"
                />
              </FormGroup>
              {/* add tool tip  */}
              <Tooltip
                title={
                  <div
                    dangerouslySetInnerHTML={{
                      __html:
                        aiResearchAgentToggletext ||
                        "Enable AI-driven research to enhance your article with relevant statistics, expert insights, case studies, emerging trends, and even data from your own website. This feature ensures your content is backed by high-quality, authoritative information, making it more engaging and credible.",
                    }}
                  />
                }
                placement="top"
              >
                <InfoIcon sx={{ fontSize: "16px", ml: -1.5 }} />
              </Tooltip>
            </Box>
            {/* a sublist of tasks for ai research agent with icons check */}
            <Box
              display={"flex"}
              alignItems={"center"}
              sx={{
                ml: "0.5rem",
                mt: "-0.5rem",
              }}
            >
              <List sx={{ px: 0.3 }}>
                <ListItem
                  sx={{
                    py: 0.3,

                    "& .MuiListItemText-root": {
                      my: 0,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {formik.values?.aiResearchAgent ? (
                      <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                    ) : (
                      <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Statistics & Quotes"
                    primaryTypographyProps={{ fontSize: "0.875rem" }}
                    sx={{
                      ml: "-0.5rem",
                    }}
                  />
                </ListItem>
                <ListItem
                  sx={{
                    py: 0.3,

                    "& .MuiListItemText-root": {
                      my: 0,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {formik.values?.aiResearchAgent ? (
                      <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                    ) : (
                      <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Thought Leadership Insights"
                    primaryTypographyProps={{ fontSize: "0.875rem" }}
                    sx={{
                      ml: "-0.5rem",
                    }}
                  />
                </ListItem>
                <ListItem
                  sx={{
                    py: 0.3,

                    "& .MuiListItemText-root": {
                      my: 0,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {formik.values?.aiResearchAgent ? (
                      <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                    ) : (
                      <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Case Studies"
                    primaryTypographyProps={{ fontSize: "0.875rem" }}
                    sx={{
                      ml: "-0.5rem",
                    }}
                  />
                </ListItem>
                <ListItem
                  sx={{
                    py: 0.3,

                    "& .MuiListItemText-root": {
                      my: 0,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {formik.values?.aiResearchAgent ? (
                      <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                    ) : (
                      <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Emerging Trends"
                    primaryTypographyProps={{ fontSize: "0.875rem" }}
                    sx={{
                      ml: "-0.5rem",
                    }}
                  />
                </ListItem>
                <ListItem
                  sx={{
                    py: 0.3,

                    "& .MuiListItemText-root": {
                      my: 0,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {formik.values?.aiResearchAgent ? (
                      <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                    ) : (
                      <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Insights from Your Website"
                    primaryTypographyProps={{ fontSize: "0.875rem" }}
                    sx={{
                      ml: "-0.5rem",
                    }}
                  />
                </ListItem>
              </List>
            </Box>

            {/* AI Eval & Refine Agent  */}
            <Box display={"flex"} alignItems={"center"}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      onChange={formik.handleChange}
                      disabled={!isAiEvalRefineAgentAvailableAtPlan}
                      checked={formik.values?.aiEvalRefineAgent}
                      // defaultChecked={interLinkingChecked}
                      name="aiEvalRefineAgent"
                      value={formik.values?.aiEvalRefineAgent}
                    />
                  }
                  label="AI Eval & Refine Agent"
                />
              </FormGroup>
              {/* add tool tip  */}
              <Tooltip
                title={
                  <div
                    dangerouslySetInnerHTML={{
                      __html: aiEvalRefineAgentToggletext,
                    }}
                  />
                }
                placement="top"
              >
                <InfoIcon sx={{ fontSize: "16px", ml: -1.5 }} />
              </Tooltip>
            </Box>
            {/* a sublist of tasks for ai eval & refine agent with icons check */}
            <Box
              display={"flex"}
              alignItems={"center"}
              sx={{
                ml: "0.5rem",
                mt: "-0.5rem",
              }}
            >
              <List sx={{ px: 0.3 }}>
                <ListItem
                  sx={{
                    py: 0.3,
                    "& .MuiListItemText-root": {
                      my: 0,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {formik.values?.aiEvalRefineAgent ? (
                      <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                    ) : (
                      <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Reviews initial article and provides feedback"
                    primaryTypographyProps={{ fontSize: "0.875rem" }}
                    sx={{
                      ml: "-0.5rem",
                    }}
                  />
                </ListItem>
                <ListItem
                  sx={{
                    py: 0.3,
                    "& .MuiListItemText-root": {
                      my: 0,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {formik.values?.aiEvalRefineAgent ? (
                      <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                    ) : (
                      <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Gathers additional data and insights"
                    primaryTypographyProps={{ fontSize: "0.875rem" }}
                    sx={{
                      ml: "-0.5rem",
                    }}
                  />
                </ListItem>
                <ListItem
                  sx={{
                    py: 0.3,
                    "& .MuiListItemText-root": {
                      my: 0,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {formik.values?.aiEvalRefineAgent ? (
                      <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                    ) : (
                      <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Rewrites & refines the final article for E-E-A-T"
                    primaryTypographyProps={{ fontSize: "0.875rem" }}
                    sx={{
                      ml: "-0.5rem",
                    }}
                  />
                </ListItem>
              </List>
            </Box>

            {/* insights toggle */}
            <Box display={"flex"} alignItems={"center"}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      onChange={handleInsightsAdded}
                      // disabled={!isInsightsAdded}
                      checked={insights}
                      defaultChecked={insights}
                    />
                  }
                  label="Your insights and instructions"
                />
              </FormGroup>
            </Box>

            {insights && (
              <Box
                // display={"flex"}
                alignItems={"center"}
                width="100%"
                // height="170px"
              >
                <Box width="100%" height="53px" top="4px" gap="4px">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography
                      color="#121828"
                      sx={{
                        fontWeight: "500",
                        fontFamily: "Inter",
                        fontSize: "16px",
                      }}
                    >
                      Add your insights and instructions
                    </Typography>
                    {articleChoice === "write" && (
                      <Tooltip
                        title="When this toggle is on and you leave the field empty, we'll automatically generate instructions using your article outline"
                        placement="top"
                      >
                        <InfoIcon sx={{ fontSize: "16px" }} />
                      </Tooltip>
                    )}
                  </Box>
                  <Typography
                    color="#7B85AD"
                    sx={{
                      fontWeight: "400",
                      fontFamily: "Inter",
                      fontSize: "12px",
                    }}
                  >
                    Add your instructions and expert insights (data, quotes,
                    personal experience) to guide AI content. Write each
                    instruction or insight on a separate line for clarity.
                  </Typography>
                </Box>
                <Box width="100%" marginTop={"10px"}>
                  <InsightsEditor
                    value={formik.values.insights}
                    onChange={(value) =>
                      formik.setFieldValue("insights", value)
                    }
                    placeholder={
                      articleChoice === "write"
                        ? "Leave empty to auto-generate instructions during article creation, or add your own..."
                        : placeholderText
                    }
                    error={
                      formik.touched.insights && Boolean(formik.errors.insights)
                    }
                    helperText={
                      formik.touched.insights && formik.errors.insights
                        ? formik.errors.insights
                        : undefined
                    }
                    compact={true}
                    maxLength={20000}
                    showResizeHandle={true}
                    {...(articleChoice !== "write" && {
                      onWriteInstructions: handleWriteInstructions,
                      isGeneratingInstructions: isWritingInstructions,
                    })}
                    dispatch={dispatch}
                  />
                </Box>
              </Box>
            )}
          </Box>
          {articleChoice === "write" ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1,
                pb: 2,
              }}
            >
              <Box sx={{ pt: 1, textAlign: "left" }}>
                {/* {isClicked.advanced &&
                Object.keys(formik.errors).length === 0 ? (
                  <Button
                    sx={{ px: 7, py: 2 }}
                    size="large"
                    variant="contained"
                    disabled
                  >
                    <CircularProgress
                      size={12}
                      color="inherit"
                      sx={{
                        zIndex: 1,
                      }}
                    />
                  </Button>
                ) : ( */}
                <Button
                  variant="outlined"
                  onClick={() => [
                    formik.setFieldValue("generateArticle", false),
                    isSubmitted("advanced", projectId),
                    setIsClicked({ ...isClicked, advanced: true }),
                  ]}
                  disabled={formik.isSubmitting || isClicked.advanced}
                  startIcon={
                    isClicked.advanced && formik.isSubmitting ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null
                  }
                >
                  {isClicked.advanced && formik.isSubmitting
                    ? "Processing..."
                    : "Advanced Mode"}
                </Button>
                {/* )} */}
              </Box>
              <Box sx={{ pt: 1, textAlign: "right" }}>
                {/* {isClicked.oneClick && !formik.errors ? (
                  <Button
                    sx={{ px: 7, py: 2 }}
                    size="large"
                    variant="contained"
                    disabled
                  >
                    <CircularProgress
                      size={12}
                      color="inherit"
                      sx={{
                        zIndex: 1,
                      }}
                    />
                  </Button>
                ) : ( */}
                <Button
                  variant="contained"
                  onClick={() => [checkError()]}
                  disabled={formik.isSubmitting || isClicked.oneClick}
                  startIcon={
                    isClicked.oneClick && formik.isSubmitting ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <MagicWandIcon sx={{ mt: 0.6 }} />
                    )
                  }
                >
                  {isClicked.oneClick && formik.isSubmitting
                    ? "Generating..."
                    : "1-Click Generator"}
                </Button>
                {/* )} */}
              </Box>
            </Box>
          ) : (
            <Box sx={{ pb: 2, pr: 2, textAlign: "right" }}>
              {formik.isSubmitting ? (
                <Button
                  sx={{ px: 7, py: 2 }}
                  size="large"
                  variant="contained"
                  disabled
                >
                  <CircularProgress
                    size={12}
                    color="inherit"
                    sx={{
                      zIndex: 1,
                    }}
                  />
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => [
                    formik.setFieldValue("generateArticle", false),
                    isSubmitted("update", projectId),
                    setIsClicked({ ...isClicked, update: true }),
                  ]}
                  disabled={formik.isSubmitting || isClicked.update}
                  startIcon={
                    isClicked.update && formik.isSubmitting ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <MagicWandIcon sx={{ mt: 0.6 }} />
                    )
                  }
                >
                  {isClicked.update && formik.isSubmitting
                    ? "Updating..."
                    : "Update Article"}
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          alignItems: "flex-end",
          width: "350px",
          backgroundColor: "#64748B1A",
          padding: "15px 24px 24px 24px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Typography
            width="19.267px"
            height="19.267px"
            flexShrink="0"
            sx={{
              cursor: "pointer",
            }}
            onClick={() => onClose()}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="15"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M2.69203 15.6063L8.66481 9.6335L2.69203 3.66072C2.46324 3.43568 2.33378 3.12787 2.33228 2.80725C2.33153 2.48588 2.45872 2.17807 2.686 1.95077C2.91329 1.72423 3.2211 1.59705 3.54248 1.59855C3.86385 1.60006 4.17091 1.72951 4.39595 1.95906L10.3664 7.93183L16.3392 1.95906C16.5642 1.72951 16.8713 1.60006 17.1927 1.59855C17.5133 1.59705 17.8218 1.72424 18.0491 1.95077C18.2764 2.17807 18.4036 2.48588 18.4021 2.80725C18.4014 3.12787 18.2719 3.43568 18.0431 3.66072L12.068 9.6335L18.0408 15.6063C18.2696 15.8313 18.399 16.1391 18.4005 16.4597C18.4013 16.7811 18.2741 17.0889 18.0468 17.3162C17.8195 17.5428 17.5117 17.6699 17.1903 17.6684C16.869 17.6669 16.5619 17.5375 16.3369 17.3079L10.3664 11.3352L4.39364 17.3079C3.9225 17.7723 3.16537 17.7693 2.69723 17.3027C2.22986 16.8353 2.2261 16.0782 2.68971 15.6063L2.69203 15.6063Z"
                fill="#7B85AD"
              />
            </svg>
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <ExclamationCircleColored
            sx={{
              fontSize: "24px",
              // marginBottom: "20px",
            }}
          />
        </Box>
        <Typography
          color="#7B85AD"
          sx={{
            fontWeight: "400",
            fontFamily: "Inter",
            fontSize: "16px",
            lineHeight: "23.94px",
          }}
        >
          {articleChoice === "write" ? (
            <>
              Let's get it rolling. Are you a quick mover or a precision pro?
              <br />
              <br />
              Decide what’s better for you.
            </>
          ) : (
            `Let's get it rolling. We will help you update your content in no time!
          `
          )}
        </Typography>
        {articleChoice === "write" && (
          <Box sx={{ mt: 2 }}>
            <Typography
              color="#121828"
              sx={{
                fontWeight: "500",
                fontFamily: "Inter",
                fontSize: "20px",
              }}
            >
              What’s the difference
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            alignItems: "center",
            mt: articleChoice === "write" ? 2 : 3,
          }}
        >
          <Typography
            color="#121828"
            sx={{
              fontWeight: "500",
              fontFamily: "Inter",
              fontSize: "16px",
            }}
          >
            {articleChoice === "write"
              ? "1-Click Generator"
              : "Smarter Updates. Stronger Content."}
          </Typography>
          {articleChoice != "write" && (
            <>
              <br />
            </>
          )}
          <Typography
            color="#7B85AD"
            sx={{
              fontWeight: "400",
              fontFamily: "Inter",
              fontSize: "16px",
              lineHeight: "23.94px",
            }}
          >
            {articleChoice === "write"
              ? "SEOwind's advanced AI creates the ideal brief and writes the article to nail the search intent."
              : "Our AI-powered update tool doesn’t just refresh. It rethinks."}
          </Typography>
        </Box>
        <Box
          sx={{
            alignItems: "center",
            mt: 2,
          }}
        >
          <Typography
            color="#121828"
            sx={{
              fontWeight: "500",
              fontFamily: "Inter",
              fontSize: "16px",
            }}
          >
            {articleChoice === "write" ? "Advanced Mode" : "How It Works"}
          </Typography>
          <Typography
            color="#7B85AD"
            sx={{
              fontWeight: "400",
              fontFamily: "Inter",
              fontSize: "16px",
              lineHeight: "23.94px",
            }}
          >
            {articleChoice === "write" ? (
              <>
                Gain full control. Start with a custom brief, leverage AI, and
                tailor your content to perfection.
                <br />
                <br />
                Ideal for content writers and SEO specialists.
              </>
            ) : (
              <>
                AI agents conduct in-depth research, analyze your content and
                competitors, and deliver strategic updates backed by real data,
                SEO insights, and E-E-A-T best practices.
                <br />
                It’s a multistep process, enhancing your content at every level
                from structure and substance to relevance and authority.
                <br />
                <br />
                Your content, upgraded with precision.
              </>
            )}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "left",
              pt: 5,
            }}
          >
            {!isEEATMode && (
              <Box
                display={"flex"}
                alignItems={"center"}
                sx={{
                  cursor: "pointer",
                }}
                onClick={() => onBack()}
              >
                <ArrowLeft
                  sx={{
                    fontSize: "20px",
                    color: "#7B85AD",
                  }}
                />
                <Typography
                  color="#7B85AD"
                  sx={{
                    fontWeight: "400",
                    fontFamily: "Inter",
                    fontSize: "14px",
                    lineHeight: "23.94px",
                    ml: 1,
                  }}
                >
                  Back
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
